import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Loader2, RefreshCw, Copy, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";

/**
 * QrPhoneButton + modal — "View on phone" feature.
 *
 * Lets an operator on a sales call flash their phone and instantly land on
 * their authenticated dashboard. Generates a single-use 10-min token via
 * `POST /api/auth/qr-token`, renders the resulting URL as a clean
 * Connexis-branded QR code with copy-to-clipboard fallback.
 *
 * Desktop-only (`lg:inline-flex`) — hidden on mobile because the use-case
 * is "I'm on my desktop and want to switch to my phone".
 */
export default function QrPhoneButton() {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="View this dashboard on your phone"
        aria-label="View on phone"
        data-testid="qr-phone-btn"
        className="hidden lg:inline-flex items-center gap-1.5 text-[11.5px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md px-2.5 h-8 transition-colors"
      >
        <Smartphone className="size-3.5" strokeWidth={1.7} />
        View on phone
      </button>
      <QrPhoneModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────── */

function QrPhoneModal({ open, onClose }) {
  const [data, setData]         = React.useState(null); // { url, expires_at }
  const [loading, setLoading]   = React.useState(false);
  const [err, setErr]           = React.useState("");
  const [copied, setCopied]     = React.useState(false);
  const [secondsLeft, setSL]    = React.useState(0);

  const mint = React.useCallback(async () => {
    setLoading(true); setErr(""); setData(null); setCopied(false);
    try {
      const { data: resp } = await api.post("/auth/qr-token");
      setData(resp);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't generate a QR code right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { if (open) mint(); }, [open, mint]);

  React.useEffect(() => {
    if (!data?.expires_at) { setSL(0); return; }
    const tick = () => {
      const left = Math.max(0, Math.floor((new Date(data.expires_at).getTime() - Date.now()) / 1000));
      setSL(left);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [data?.expires_at]);

  const handleCopy = async () => {
    if (!data?.url) return;
    const ok = await copyToClipboard(data.url);
    if (ok) {
      setCopied(true);
      toast.success("Link copied — paste it on your phone to sign in.");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const mm = String(Math.floor(secondsLeft / 60));
  const ss = String(secondsLeft % 60).padStart(2, "0");

  // Truncate URL in the MIDDLE so both ends stay visible (looks much cleaner
  // than ellipsis-on-end for connex.is/m/<random-token>).
  const niceUrl = React.useMemo(() => {
    if (!data?.url) return "";
    const stripped = data.url.replace(/^https?:\/\//, "");
    if (stripped.length <= 40) return stripped;
    return stripped.slice(0, 22) + "…" + stripped.slice(-10);
  }, [data?.url]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-[380px] rounded-2xl p-0 overflow-hidden border-slate-200 [&>button]:hidden"
        data-testid="qr-phone-modal"
      >
        {/* a11y-only — visible content uses h2 + p */}
        <DialogTitle className="sr-only">Open this dashboard on your phone</DialogTitle>
        <DialogDescription className="sr-only">Scan the QR code with your phone camera to sign in instantly without a password.</DialogDescription>

        {/* Close — top-right, generous tap target */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 inline-flex items-center justify-center size-8 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          aria-label="Close"
          data-testid="qr-phone-modal-close"
        >
          <X className="size-4" />
        </button>

        {/* Body — perfect vertical rhythm: 28px top, gap-y between sections, 24px bottom */}
        <div className="px-7 pt-8 pb-7 flex flex-col items-center text-center">

          {/* Phone icon → title → subtitle (tight cluster) */}
          <div className="inline-flex items-center justify-center size-11 rounded-full bg-slate-900 text-white">
            <Smartphone className="size-[18px]" strokeWidth={1.8} />
          </div>
          <h2 className="mt-4 text-[17px] font-semibold tracking-[-0.01em] text-slate-900">
            Open on your phone
          </h2>
          <p className="mt-1.5 text-[12.5px] leading-[1.5] text-slate-500 max-w-[260px]">
            Scan with your phone's camera — you'll land here already signed in.
          </p>

          {/* QR — clean, no heavy card; just a hairline border for definition */}
          <div className="mt-6 p-3 rounded-2xl border border-slate-200 bg-white" data-testid="qr-frame">
            {loading || !data ? (
              <div className="grid place-items-center size-[220px] text-slate-300" data-testid="qr-loading">
                <Loader2 className="size-6 animate-spin" />
              </div>
            ) : err ? (
              <div className="grid place-items-center size-[220px] text-rose-500 text-xs px-4 text-center" data-testid="qr-error">
                {err}
              </div>
            ) : (
              <QRCodeSVG
                value={data.url}
                size={220}
                level="H"
                fgColor="#0F172A"
                bgColor="#ffffff"
                marginSize={0}
                imageSettings={{
                  // Authentic Connexis brand mark — 3 horizontal lines + 2 brand-blue dots,
                  // pixel-aligned with the sidebar / marketing header logo.
                  src: "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 28 28'><rect width='28' height='28' rx='6' fill='%230F172A'/><g stroke='white' stroke-width='1.9' stroke-linecap='round' fill='none'><path d='M7 9h7'/><path d='M7 14h12'/><path d='M7 19h7'/></g><circle cx='21' cy='9' r='1.9' fill='%230055FF'/><circle cx='21' cy='19' r='1.9' fill='%230055FF'/></svg>",
                  height: 42,
                  width: 42,
                  excavate: true,
                }}
                data-testid="qr-svg"
              />
            )}
          </div>

          {/* Countdown — subtle, exact same width axis as everything else */}
          {data && !err && (
            <div
              className="mt-5 inline-flex items-center gap-1.5 text-[11px] font-mono tracking-wide text-slate-500"
              data-testid="qr-expires"
            >
              <span className={`size-1 rounded-full ${secondsLeft > 60 ? "bg-emerald-500" : "bg-amber-500 animate-pulse"}`} />
              Expires in {mm}:{ss}
            </div>
          )}

          {/* URL pill — middle-truncated so both ends stay legible; whole pill is the copy CTA */}
          {data && !err && (
            <button
              type="button"
              onClick={handleCopy}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 h-10 px-4 rounded-full bg-slate-50 border border-slate-200 text-[12px] font-mono text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-colors"
              data-testid="qr-copy-btn"
              aria-label="Copy sign-in link"
            >
              {copied ? (
                <><Check className="size-3.5 text-emerald-600" /> <span className="text-emerald-700 font-sans">Link copied</span></>
              ) : (
                <><Copy className="size-3.5 text-slate-500" /> <span data-testid="qr-url">{niceUrl}</span></>
              )}
            </button>
          )}

          {/* Generate new — text-only action so it doesn't compete with the URL pill */}
          <button
            type="button"
            onClick={mint}
            disabled={loading}
            className="mt-3 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-slate-500 hover:text-slate-900 disabled:opacity-50 transition-colors"
            data-testid="qr-regen-btn"
          >
            <RefreshCw className={`size-3 ${loading ? "animate-spin" : ""}`} /> Generate new code
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
